import { useMemo, useState } from "react";
import ReferenceApplyBar from "../EmissionFactorBreakdown/ReferenceApplyBar";
import ReferenceLink from "../EmissionFactorBreakdown/ReferenceLink";
import FactorForm from "./FactorForm";
import { applyEnergyFactorReference, deleteEnergyFactor } from "./energyApi";
import {
  EMPTY_FACTOR_FILTERS,
  ENERGY_REFERENCE_TARGETS,
  FACTOR_LINK_FILTERS,
  draftFromFactor,
  emptyFactorDraft,
  energyPerUnit,
  filterFactors,
  formatValue,
} from "./helpers";

/**
 * A value with the reference it is quoted from underneath.
 *
 * @param {object} props
 * @param {number|null} props.value
 * @param {import("./helpers").Reference|null} props.reference
 */
function ValueWithSource({ value, reference }) {
  return (
    <>
      <div className="enf-value">{formatValue(value)}</div>
      {reference ? <ReferenceLink reference={reference} /> : <span className="gdb-missing">No link</span>}
    </>
  );
}

/**
 * The energy factors with their density, calorific value and the source of
 * each, filtered by KPI, source and link; factors can be ticked to give many
 * of them one reference at once, added, edited and deleted.
 *
 * @param {object} props
 * @param {{ rows: import("./helpers").EnergyFactor[], loading: boolean, error: string, reload: () => Promise<void> }} props.factors
 * @param {import("./helpers").EnergySource[]} props.sources
 * @param {import("./helpers").EnergyKpi[]} props.kpis
 * @param {import("../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {() => void} props.onReferencesChanged
 * @param {() => void} props.onFactorsChanged refreshes what counts factors, such as the source list
 */
export default function FactorsTab({ factors, sources, kpis, references, onReferencesChanged, onFactorsChanged }) {
  const [filters, setFilters] = useState(EMPTY_FACTOR_FILTERS);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [formDraft, setFormDraft] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const shown = useMemo(() => filterFactors(factors.rows, filters), [factors.rows, filters]);
  const picked = shown.filter((factor) => selectedIds.has(factor.id));
  const allPicked = shown.length > 0 && picked.length === shown.length;
  const filtered = filters.search || filters.sourceId || filters.link;

  const setFilter = (key) => (e) => {
    const { value } = e.target;
    setFilters((current) => ({ ...current, [key]: value }));
  };

  /** @param {number} id */
  const toggle = (id) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelectedIds(allPicked ? new Set() : new Set(shown.map((factor) => factor.id)));

  /** @param {import("./helpers").FactorDraft} draft */
  const openForm = (draft) => {
    setNotice(null);
    setFormDraft(draft);
  };

  const changed = () => {
    factors.reload();
    onFactorsChanged();
    onReferencesChanged();
  };

  /** @param {import("./helpers").EnergyFactor} saved */
  const afterSave = (saved) => {
    setFormDraft(null);
    setNotice({ text: `${saved.kpi_title || "Energy factor"} saved.`, failed: false });
    changed();
  };

  /** @param {import("./helpers").EnergyFactor} factor */
  const remove = async (factor) => {
    const name = `${factor.kpi_title || "this"} factor in ${factor.source_name || "its source"}`;
    if (!window.confirm(`Delete the ${name}? Companies priced from it will have no energy factor for this KPI.`)) return;
    setNotice(null);
    setDeletingId(factor.id);
    const res = await deleteEnergyFactor(factor.id);
    setDeletingId(null);
    if (!res?.isSuccess) {
      setNotice({ text: res?.data?.error || `Couldn't delete the ${name}.`, failed: true });
      return;
    }
    setNotice({ text: `The ${name} was deleted.`, failed: false });
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(factor.id);
      return next;
    });
    changed();
  };

  /**
   * @param {number|null} referenceId
   * @param {string[]} targets
   * @returns {Promise<boolean>}
   */
  const applyReference = async (referenceId, targets) => {
    const res = await applyEnergyFactorReference({ reference_id: referenceId, targets, factor_ids: picked.map((factor) => factor.id) });
    if (!res?.isSuccess) return false;
    setSelectedIds(new Set());
    factors.reload();
    onReferencesChanged();
    return true;
  };

  return (
    <div className="gdb-root" role="tabpanel" id="enf-panel-factors" aria-labelledby="enf-tab-factors">
      <div className="enf-tabbar-actions">
        <p className="gdb-lead">How a reading becomes energy, per KPI and source. Tick factors to give them one source link.</p>
        {formDraft ? null : (
          <button
            type="button"
            className="kgb-btn kgb-btn--primary"
            disabled={!sources.length}
            title={sources.length ? undefined : "Add an energy source first"}
            onClick={() => openForm(emptyFactorDraft(filters.sourceId))}
          >
            + Add factor
          </button>
        )}
      </div>

      {formDraft ? (
        <FactorForm
          key={formDraft.id ?? "new"}
          initial={formDraft}
          factors={factors.rows}
          kpis={kpis}
          sources={sources}
          references={references}
          onReferencesChanged={onReferencesChanged}
          onSaved={afterSave}
          onCancel={() => setFormDraft(null)}
        />
      ) : null}

      {notice ? (
        <p className={`gdb-notice${notice.failed ? " gdb-notice--error" : ""}`} role={notice.failed ? "alert" : "status"}>
          {notice.text}
        </p>
      ) : null}

      <div className="kgb-card gdb-toolbar">
        <label className="gdb-field gdb-field--grow" htmlFor="enf-search">
          <span>Search</span>
          <input id="enf-search" type="search" value={filters.search} placeholder="KPI or fuel type" onChange={setFilter("search")} />
        </label>
        <label className="gdb-field" htmlFor="enf-filter-source">
          <span>Energy source</span>
          <select id="enf-filter-source" value={filters.sourceId} onChange={setFilter("sourceId")}>
            <option value="">Any source</option>
            {sources.map((source) => (
              <option key={source.id} value={String(source.id)}>
                {source.name}
              </option>
            ))}
          </select>
        </label>
        <label className="gdb-field" htmlFor="enf-filter-link">
          <span>Source link</span>
          <select id="enf-filter-link" value={filters.link} onChange={setFilter("link")}>
            {FACTOR_LINK_FILTERS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {filtered ? (
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={() => setFilters(EMPTY_FACTOR_FILTERS)}>
            Clear filters
          </button>
        ) : null}
      </div>

      {picked.length ? (
        <ReferenceApplyBar
          count={picked.length}
          allMatching={allPicked}
          totalFactors={shown.length}
          references={references}
          targets={ENERGY_REFERENCE_TARGETS}
          initialTargets={["density", "calorific"]}
          onSelectAllMatching={() => setSelectedIds(new Set(shown.map((factor) => factor.id)))}
          onClear={() => setSelectedIds(new Set())}
          onApply={applyReference}
          onReferencesChanged={onReferencesChanged}
        />
      ) : null}

      {factors.error ? (
        <div className="gdb-message gdb-message--error">
          {factors.error}{" "}
          <button type="button" className="kgb-btn kgb-btn--quiet" onClick={factors.reload}>
            Retry
          </button>
        </div>
      ) : null}
      {!factors.error && factors.loading ? <div className="gdb-message">Loading energy factors…</div> : null}
      {!factors.error && !factors.loading && !shown.length ? (
        <div className="gdb-message">{filtered ? "No factor matches these filters." : "No energy factors yet."}</div>
      ) : null}

      {shown.length ? (
        <div className="kgb-card gdb-table-wrap">
          <table className="gdb-table">
            <thead>
              <tr>
                <th scope="col" className="enf-check">
                  <input type="checkbox" aria-label="Select every factor shown" checked={allPicked} onChange={toggleAll} />
                </th>
                <th scope="col">KPI</th>
                <th scope="col">Source</th>
                <th scope="col">Unit</th>
                <th scope="col">Fuel density</th>
                <th scope="col">Calorific value</th>
                <th scope="col" className="gdb-num">
                  Energy factor
                </th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {shown.map((factor) => (
                <tr key={factor.id} className={formDraft?.id === factor.id ? "gdb-row--editing" : undefined}>
                  <td className="enf-check">
                    <input
                      type="checkbox"
                      aria-label={`Select ${factor.kpi_title || `factor ${factor.id}`} in ${factor.source_name || "no source"}`}
                      checked={selectedIds.has(factor.id)}
                      onChange={() => toggle(factor.id)}
                    />
                  </td>
                  <td>
                    <div className="gdb-name">{factor.kpi_title || `KPI ${factor.kpi_id}`}</div>
                    {factor.fuel_type ? <div className="gdb-muted">{factor.fuel_type}</div> : null}
                  </td>
                  <td>
                    <div>{factor.source_name || "—"}</div>
                    <div className="gdb-muted">{factor.country_code}</div>
                  </td>
                  <td className="enf-mono">{factor.unit}</td>
                  <td>
                    <ValueWithSource value={factor.fuel_density} reference={factor.density_reference} />
                  </td>
                  <td>
                    <ValueWithSource value={factor.calorific_value} reference={factor.calorific_reference} />
                  </td>
                  <td className="gdb-num">
                    <span className="enf-value">{formatValue(energyPerUnit(factor))}</span>
                    <div className="gdb-muted">GJ / {factor.unit}</div>
                  </td>
                  <td className="gdb-actions">
                    <button
                      type="button"
                      className="kgb-btn"
                      aria-label={`Edit ${factor.kpi_title || `factor ${factor.id}`}`}
                      onClick={() => openForm(draftFromFactor(factor))}
                    >
                      Edit
                    </button>{" "}
                    <button
                      type="button"
                      className="kgb-btn kgb-btn--quiet kgb-btn--danger"
                      aria-label={`Delete ${factor.kpi_title || `factor ${factor.id}`}`}
                      disabled={deletingId === factor.id}
                      onClick={() => remove(factor)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
